import { useCallback, useEffect, useRef, type ReactElement } from 'react';
import { Button } from '@/components/atoms/button';
import type { ArrangementSummary, SourceSummary } from '@/lib/queries/moderation';

const HEADING_ID = 'merge-candidate-modal-heading';
const PERCENT_FACTOR = 100;

const COPY = {
  heading: 'Merge into existing piece',
  bodyPrefix: 'Merge ',
  bodySuffix: ' into one of these pieces.',
  empty: 'No merge targets found for this candidate.',
  searchLabel: 'Search any piece by title',
  searchPlaceholder: 'Search any piece by title…',
  arrangementsLegend: 'Arrangements to transfer',
  sourcesLegend: 'Sources to transfer',
  confirm: 'Merge',
  confirming: 'Merging…',
  cancel: 'Cancel',
} as const;

export type MergeTargetOption = {
  pieceId: string;
  prettyName: string;
  composerName: string | null;
  similarity: number;
};

export type MergeCandidateModalProps = {
  candidateTitle: string;
  targets: MergeTargetOption[];
  selectedTargetId: string | null;
  onSelectTarget: (targetId: string) => void;
  arrangements: ArrangementSummary[];
  sources: SourceSummary[];
  selectedArrangementIds: string[];
  selectedSourceIds: string[];
  onToggleArrangement: (arrangementId: string) => void;
  onToggleSource: (pieceSourceId: string) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchResults: MergeTargetOption[];
  isMerging: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

function formatSimilarity(similarity: number): string {
  return `${Math.round(similarity * PERCENT_FACTOR)}%`;
}

function arrangementLabel(arrangement: ArrangementSummary): string {
  return arrangement.keyName
    ? `${arrangement.prettyName} · ${arrangement.keyName}`
    : arrangement.prettyName;
}

function sourceLabel(source: SourceSummary): string {
  return source.note ? `${source.value} · ${source.note}` : source.value;
}

export function MergeCandidateModal({
  candidateTitle,
  targets,
  selectedTargetId,
  onSelectTarget,
  arrangements,
  sources,
  selectedArrangementIds,
  selectedSourceIds,
  onToggleArrangement,
  onToggleSource,
  searchValue,
  onSearchChange,
  searchResults,
  isMerging,
  onConfirm,
  onCancel,
}: MergeCandidateModalProps): ReactElement {
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  const handleEscape = useCallback(
    (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancel();
    },
    [onCancel],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const targetPieceIds = new Set(targets.map((target) => target.pieceId));
  const extraResults = searchResults.filter((result) => !targetPieceIds.has(result.pieceId));
  const visibleTargets = [...targets, ...extraResults];
  const hasTargets = visibleTargets.length > 0;
  const canMerge = hasTargets && selectedTargetId !== null && !isMerging;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center">
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={HEADING_ID}
        className="relative w-full max-w-2xl space-y-5 rounded-t-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-surface)] p-6 shadow-xl sm:rounded-[var(--r-md)]"
      >
        <div>
          <h2
            id={HEADING_ID}
            className="font-display text-xl font-semibold tracking-tight text-[var(--ink)]"
          >
            {COPY.heading}
          </h2>
          <p className="mt-2 text-sm text-[var(--ink-3)]">
            {COPY.bodyPrefix}
            <span className="font-medium text-[var(--ink)]">{candidateTitle}</span>
            {COPY.bodySuffix}
          </p>
        </div>

        <label className="block">
          <span className="sr-only">{COPY.searchLabel}</span>
          <input
            type="text"
            aria-label={COPY.searchLabel}
            placeholder={COPY.searchPlaceholder}
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            className="w-full rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--ink)]"
          />
        </label>

        {hasTargets ? (
          <ul className="max-h-72 space-y-2 overflow-y-auto">
            {visibleTargets.map((target) => {
              const isSelected = target.pieceId === selectedTargetId;
              return (
                <li key={target.pieceId}>
                  <button
                    type="button"
                    aria-current={isSelected ? 'true' : undefined}
                    onClick={() => onSelectTarget(target.pieceId)}
                    className={`flex w-full items-center justify-between gap-3 rounded-[var(--r-sm)] border p-3 text-left transition-colors ${
                      isSelected
                        ? 'border-[var(--accent)] bg-[var(--bg-soft)]'
                        : 'border-[var(--rule)] hover:bg-[var(--bg-soft)]'
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-[var(--ink)]">
                        {target.prettyName}
                      </span>
                      <span className="block truncate text-[12px] text-[var(--ink-3)]">
                        {target.composerName ?? 'Unknown composer'}
                      </span>
                    </span>
                    <span className="shrink-0 text-[12px] font-medium text-[var(--ink-3)]">
                      {formatSimilarity(target.similarity)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-soft)] p-3 text-sm text-[var(--ink-3)]">
            {COPY.empty}
          </p>
        )}

        {arrangements.length > 0 && (
          <fieldset className="space-y-2">
            <legend className="text-[12px] font-semibold uppercase tracking-wide text-[var(--ink-3)]">
              {COPY.arrangementsLegend}
            </legend>
            {arrangements.map((arrangement) => (
              <label
                key={arrangement.arrangementId}
                className="flex items-center gap-2 text-sm text-[var(--ink)]"
              >
                <input
                  type="checkbox"
                  checked={selectedArrangementIds.includes(arrangement.arrangementId)}
                  onChange={() => onToggleArrangement(arrangement.arrangementId)}
                />
                <span className="truncate">{arrangementLabel(arrangement)}</span>
              </label>
            ))}
          </fieldset>
        )}

        {sources.length > 0 && (
          <fieldset className="space-y-2">
            <legend className="text-[12px] font-semibold uppercase tracking-wide text-[var(--ink-3)]">
              {COPY.sourcesLegend}
            </legend>
            {sources.map((source) => (
              <label
                key={source.pieceSourceId}
                className="flex items-center gap-2 text-sm text-[var(--ink)]"
              >
                <input
                  type="checkbox"
                  checked={selectedSourceIds.includes(source.pieceSourceId)}
                  onChange={() => onToggleSource(source.pieceSourceId)}
                />
                <span className="truncate">{sourceLabel(source)}</span>
              </label>
            ))}
          </fieldset>
        )}

        <div className="flex items-center justify-end gap-3">
          <Button ref={cancelRef} variant="secondary" size="sm" onClick={onCancel}>
            {COPY.cancel}
          </Button>
          <Button variant="primary" size="sm" onClick={onConfirm} disabled={!canMerge}>
            {isMerging ? COPY.confirming : COPY.confirm}
          </Button>
        </div>
      </div>
    </div>
  );
}
